// Today's four cards on top (6.3, desktop): Due this week, Meetings this week, Awaiting approval, Knowledge base.
// One small call (GET /api/today/cards); each card opens where its number comes from. The week is Sunday to
// Thursday (on Friday and Saturday, the coming one), so the Calendar opens on that week.
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { TodayCards as Cards } from '@dpw/shared';
import { api } from '../../api';
import { localDate, localDay, localTime } from '../../lib/format';
import { Skel } from '../../ui/motion';
import { weekdayOf } from './util';

const nextWhen = (iso: string) => {
  const d = localDay(iso);
  return `${d === localDate() ? 'today' : weekdayOf(d)} ${localTime(iso)}`;
};

export function TodayCards() {
  const q = useQuery({ queryKey: ['today-cards'], queryFn: api.today.cards });
  if (q.error) return null;
  if (!q.data) {
    return (
      <div className="tcards" role="status" aria-label="Loading">
        {[0, 1, 2, 3].map(i => <div className="tcard" key={i}><Skel w="50%" h={11} /><Skel w="40%" h={30} r={8} /><Skel w="60%" h={12} /></div>)}
      </div>
    );
  }
  const c: Cards = q.data;
  const week = `/calendar?view=week&date=${c.week.from}`;
  return (
    <nav className="tcards" aria-label="This week">
      <Link className="tcard" to={week} data-card="due" aria-label={`Due this week: ${c.due.open} open, ${c.due.yours} yours. Open the calendar`}>
        <span className="tck">Due this week</span>
        <span className="tcn"><b>{c.due.open}</b><span>{c.due.open === 1 ? 'activity open' : 'activities open'}</span></span>
        <span className="tcs">{c.due.yours} {c.due.yours === 1 ? 'is' : 'are'} yours</span>
      </Link>
      <Link className="tcard" to={week} data-card="meetings" aria-label={`Meetings this week: ${c.meetings.total}. Open the calendar`}>
        <span className="tck">Meetings this week</span>
        <span className="tcn"><b>{c.meetings.total}</b><span>{c.meetings.total === 1 ? 'meeting' : 'meetings'}</span></span>
        <span className="tcs">{c.meetings.next ? `Next: ${c.meetings.next.title}, ${nextWhen(c.meetings.next.startsAt)}` : 'No more this week'}</span>
      </Link>
      <Link className="tcard" to="/requests" data-card="approvals" aria-label={`Awaiting approval: ${c.approvals.total}. Review now`}>
        <span className="tck">Awaiting approval</span>
        <span className="tcn"><b>{c.approvals.total}</b><span>{c.approvals.total ? c.approvals.mix.map(m => `${m.n} ${m.word}`).join(', ') : 'nothing waiting'}</span></span>
        <span className="tcs tcgo">{c.approvals.total ? 'Review now' : 'Open Requests'}</span>
      </Link>
      <Link className="tcard" to="/knowledge" data-card="knowledge" aria-label={`Knowledge base: ${c.knowledge.kept} verified this week. Open the Research Center`}>
        <span className="tck">Knowledge base</span>
        <span className="tcn"><b>{c.knowledge.kept}</b><span>verified this week</span></span>
        <span className="tcs">{c.knowledge.toReview === null ? 'Kept insights in the Research Center' : `${c.knowledge.toReview} new to review`}</span>
      </Link>
    </nav>
  );
}
